import React, { useEffect, useState } from 'react';
import SplashScreen from './components/SplashScreen';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { prefetchAll, prefetchHome } from './core/prefetch';
import { LangProvider } from './core/i18n';
import { useAuth } from './core/firebase/useAuth';
import { AuthModalProvider, useAuthModal } from './core/AuthModalContext';

import './components/layout.css';

import TopBar    from './components/TopBar';
import BottomNav from './components/BottomNav';
import Sidebar   from './components/Sidebar';

import AccueilPage        from './pages/accueil/AccueilPage';
import MessagesPage       from './pages/messages/MessagesPage';
import VideosPage         from './pages/videos/VideosPage';
import ExplorePage        from './pages/explore/ExplorePage';
import CreerPage          from './pages/creer/CreerPage';
import ProfilePage        from './pages/profile/ProfilePage';
import AuthPage           from './pages/auth/AuthPage';
import CategoryPage       from './pages/category/CategoryPage';
import SettingsPage       from './pages/settings/SettingsPage';
import NotificationsPage  from './pages/notifications/NotificationsPage';
import ProductDetailPage  from './pages/product/ProductDetailPage';

// Affiche le modal d'auth automatiquement si pas connecté
function AuthGuard() {
  const { user, loading } = useAuth();
  const { showAuthModal } = useAuthModal();
  const { pathname }      = useLocation();

  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (pathname === '/login') return;
    if (pathname === '/videos') return;

    // Une seule fois par session
    const dismissed = sessionStorage.getItem('auth_modal_seen');
    if (dismissed) return;

    const t = setTimeout(() => {
      showAuthModal();
      sessionStorage.setItem('auth_modal_seen', '1');
    }, 1200);

    return () => clearTimeout(t);
  }, [loading, user, pathname]); // eslint-disable-line

  return null;
}

function AppLayout() {
  const { pathname } = useLocation();
  const isAuth = pathname === '/login';

  if (isAuth) {
    return (
      <div style={{ background: '#f5f7ff', minHeight: '100vh' }}>
        <AuthPage />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <AuthGuard />

      <div className="app-topbar">
        <TopBar />
      </div>

      <Sidebar />

      <main className="app-body">
        <Routes>
          <Route path="/"                  element={<AccueilPage />} />
          <Route path="/category/:slug"    element={<CategoryPage />} />
          <Route path="/messages"          element={<MessagesPage />} />
          <Route path="/videos"            element={<VideosPage />} />
          <Route path="/explore"           element={<ExplorePage />} />
          <Route path="/creer"             element={<CreerPage />} />
          <Route path="/profile"           element={<ProfilePage />} />
          <Route path="/settings"          element={<SettingsPage />} />
          <Route path="/notifications"     element={<NotificationsPage />} />
          <Route path="/product/:col/:id"  element={<ProductDetailPage />} />
          <Route path="/login"             element={<AuthPage />} />
        </Routes>
      </main>

      <div className="app-bottom-nav">
        <BottomNav />
      </div>
    </div>
  );
}

export default function App() {
  const showSplash = !sessionStorage.getItem('splash_done');
  const [splash,  setSplash]  = useState(showSplash);
  const [prodReady, setProdReady] = useState(false);

  useEffect(() => {
    if (showSplash) {
      // charge max de produits pendant l'animation, signale quand c'est prêt
      prefetchHome().then(() => setProdReady(true)).catch(() => setProdReady(true));
      prefetchAll();
    } else {
      prefetchAll();
    }
  }, []); // eslint-disable-line

  const handleSplashDone = () => {
    sessionStorage.setItem('splash_done', '1');
    setSplash(false);
  };

  if (splash) return <SplashScreen ready={prodReady} onDone={handleSplashDone} />;

  return (
    <BrowserRouter>
      <LangProvider>
        <AuthModalProvider>
          <AppLayout />
        </AuthModalProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
